/**
 * Plant Mapping Statistics Component
 * Shows statistics and metrics for the plant mapping
 */

import React from 'react';
import { PlantMappingDashboard } from '../../services/plantMapping';
import { BarChart3, TrendingUp, MapPin, Users, Target, Clock } from 'lucide-react';

interface PlantMappingStatsProps {
  statistics: PlantMappingDashboard['statistics'];
}

const PlantMappingStats: React.FC<PlantMappingStatsProps> = ({ statistics }) => {
  // Calculate percentages
  const positionedPercentage = statistics.total_workshops > 0 
    ? (statistics.positioned_workshops / statistics.total_workshops) * 100 
    : 0;

  const unpositionedPercentage = 100 - positionedPercentage;

  return (
    <div className="p-4 border-t">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistics</h3>
      
      {/* Overview Cards */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="bg-blue-50 p-3 rounded">
          <div className="flex items-center gap-2 mb-1">
            <MapPin size={16} className="text-blue-600" />
            <span className="text-xs font-medium text-blue-800">Positioned</span>
          </div>
          <div className="text-xl font-bold text-blue-900">
            {statistics.positioned_workshops}
          </div>
          <div className="text-xs text-blue-700">
            {positionedPercentage.toFixed(1)}%
          </div>
        </div>

        <div className="bg-orange-50 p-3 rounded">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={16} className="text-orange-600" />
            <span className="text-xs font-medium text-orange-800">Pending</span>
          </div>
          <div className="text-xl font-bold text-orange-900">
            {statistics.unpositioned_workshops}
          </div>
          <div className="text-xs text-orange-700">
            {unpositionedPercentage.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between text-xs text-gray-600 mb-1">
          <span>Positioning Progress</span>
          <span>{positionedPercentage.toFixed(1)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${positionedPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* Status Breakdown */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
          <BarChart3 size={14} />
          By Status
        </h4>
        <div className="space-y-2">
          {statistics.workshops_by_status.map((item, index) => {
            const percentage = statistics.total_workshops > 0 
              ? (item.count / statistics.total_workshops) * 100 
              : 0;
            
            const statusColors = {
              'planned': 'bg-gray-500',
              'ongoing': 'bg-blue-500',
              'completed': 'bg-green-500',
              'cancelled': 'bg-red-500'
            };
            
            const colorClass = statusColors[item.status as keyof typeof statusColors] || 'bg-gray-500';
            
            return (
              <div key={index} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${colorClass}`}></div>
                  <span className="capitalize">{item.status}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{item.count}</span>
                  <span className="text-gray-500">({percentage.toFixed(1)}%)</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Department Breakdown */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
          <Users size={14} />
          By Department
        </h4>
        <div className="space-y-2 max-h-32 overflow-y-auto">
          {statistics.workshops_by_department.map((item, index) => {
            const percentage = statistics.total_workshops > 0 
              ? (item.count / statistics.total_workshops) * 100 
              : 0;
            
            return (
              <div key={index} className="flex items-center justify-between text-xs">
                <div className="flex-1 truncate">
                  <span>{item.department__name || 'No Department'}</span>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  <span className="font-medium">{item.count}</span>
                  <span className="text-gray-500">({percentage.toFixed(1)}%)</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Zone Information */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
          <Target size={14} />
          Zones
        </h4>
        <div className="bg-gray-50 p-2 rounded text-xs">
          <div className="flex justify-between">
            <span>Total Zones:</span>
            <span className="font-medium">{statistics.total_zones}</span>
          </div>
        </div>
      </div>

      {/* Quick Insights */}
      <div className="bg-green-50 p-3 rounded">
        <h5 className="text-xs font-medium text-green-800 mb-2 flex items-center gap-1">
          <TrendingUp size={12} />
          Insights
        </h5>
        <div className="space-y-1 text-xs text-green-700">
          {positionedPercentage >= 80 && (
            <div>✓ Most workshops are positioned</div>
          )}
          {positionedPercentage < 50 && (
            <div>⚠ Many workshops need positioning</div>
          )}
          {statistics.total_zones === 0 && (
            <div>💡 Consider creating zones for organization</div>
          )}
          {statistics.total_zones > 0 && (
            <div>📍 {statistics.total_zones} zones defined</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlantMappingStats;
